import React, { useCallback } from 'react';

import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import CustomGeneralPhotos from '../../custom-general/custom-general-photos';
import Checklist from '../checklist';
import CustomUTMAnomaly from '../../custom-utm/customUTMAnomaly';
import EquipmentAnomalySummaryItem from '../equipment-anomaly-summary-item';
import FVIPDOChecklistReport from '../visual-inspection-pdo/fvi-pdo-checklist-report';
import GeneralDrawing from '../general-drawing';
import PID from '../pid';
import PDOSummaryReport from '../pdo-summary';
import RiskRating from '../../../pages/reports/riskRating';

import { getSplitData, matchPhrases } from '../../../../utils/utilities';
import { FVIEquipmentItem, PDFImageProps } from '../../../../utils/models';

import config from '../../../../utils/config';

interface EquipmentAnalyticsProps {
	items: FVIEquipmentItem[];
	includePDO?: boolean;
	includeEmptyPDONotice?: boolean
	useGroupedTitle?: boolean;
}

const EquipmentAnalytics: React.FC<EquipmentAnalyticsProps> = ({ items, includePDO = false, includeEmptyPDONotice = false, useGroupedTitle = false }) => {
	const  getChecklistHeaders = useCallback((item: FVIEquipmentItem) => {
		return [
			{
				label: 'Equipment Tag',
				value: item.equipmentNumber
			},
			{
				label: 'Equipment Description',
				value: item.equipmentDescription ?? 'Not Available'
			},
		];
	}, []);

	const getAnomalyHeaders = useCallback((item: FVIEquipmentItem) => {
        return [
            {
                label: 'Equipment Tag',
                value: item.equipmentNumber,
            },
            {
                label: 'Equipment Description',
                value: item.equipmentDescription ?? '',
            },
        ];
    }, []);

	return (
		<>
			<PDOSummaryReport 
				includeEmptyReportNotice={includeEmptyPDONotice} 
				itemKey='equipmentNumber' 
				itemLabel='Equipment Tag' 
				items={items} 
			/>

			{items.map((item) => (
				<React.Fragment key={item.equipmentNumber}>
					{getSplitData<PDFImageProps>(item.pIDImages, 1)
						.map((pid, index: number) => (
							<PID 
								hasPID={item.hasPID}
								key={index} 
								pidImages={pid} 
								pidNumber={item.pIDNumber} 
								sectionTitle={useGroupedTitle ? "Visual Inspection Scope" : "P & ID"}
								sectionSubtitle={useGroupedTitle ? "P & ID" : undefined} 
								identifier={item.equipmentNumber}
							/>
						))}

					<GeneralDrawing identifier={item.equipmentNumber} generalDrawing={item.generalDrawing ?? []} />

					<Conditional isVisible={matchPhrases(item.inspectionType, 'external')}>
						<Checklist
							enableChecklistItemComment={false}
							headers={getChecklistHeaders(item)}
							checklistItems={item.checklistItems || []}
							additionalComment={item.additionalComment}
							sectionTitle={useGroupedTitle ? "Visual Inspection Scope" : "Checklist"}
							sectionSubtitle={useGroupedTitle ? "Checklist" : undefined} 
						/>
					</Conditional>

					{item.pdoChecklist && (
						<Conditional isVisible={item.pdoChecklist.length > 0}>
							<FVIPDOChecklistReport
								items={item.pdoChecklist}
								label="Equipment Tag"
								value={item.equipmentNumber}
								hasPDO={matchPhrases(item.hasPDO, 'yes')}
							/>
						</Conditional>
					)}

					<EquipmentAnomalySummaryItem 
						equipmentItem={item} 
						includeSeparator={false}
						sectionTitle={useGroupedTitle ? "Visual Inspection Scope" : "Anomaly Summary & Description"}
						sectionSubtitle={useGroupedTitle ? "Anomaly Summary & Description" : undefined} 
					/>

					<CustomUTMAnomaly
						sectionTitle={useGroupedTitle ? "Visual Inspection Scope" : "Anomaly Report"}
						sectionSubtitle={useGroupedTitle ? "Anomaly Report" : undefined} 
						lineNumberID={item.id}
						includePDO={includePDO}
						anomalyItems={item.anomalyItems}
						headers={getAnomalyHeaders(item)}
					/>

					<Conditional isVisible={item.generalPhotos.length > 0}>
						<CustomGeneralPhotos
							identifier={item.equipmentNumber}
							label={useGroupedTitle ? "Visual Inspection Scope" : "General Photos"}
							subLabel={useGroupedTitle ? "General Photos" : undefined} 
							photos={item.generalPhotos}
						/>
					</Conditional>
				</React.Fragment>
			))}
			
			<Conditional isVisible={config.enableRiskRating}>
				<CustomSection sectionTitle="Risk Rating">
					<Conditional isVisible={items.length > 0}>
						<RiskRating data={items} />
					</Conditional>
				</CustomSection>
			</Conditional>
		</>
	);
};



export default EquipmentAnalytics;
